import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, Clock, Tag, PenSquare, ArrowRight, User } from "lucide-react";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { fetchBlogArticles, fetchBlogCategories } from "../lib/api";

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogCategories().then((d) => setCategories(d.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchBlogArticles({ category: selectedCat || undefined, search: search || undefined })
      .then((d) => setArticles(d.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [selectedCat, search]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog Autopièce Malin",
    url: `${origin}/blog`,
    description:
      "Guides, astuces et témoignages pour trouver la bonne pièce détachée auto, moto ou poids lourd.",
    blogPost: articles.slice(0, 10).map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      url: `${origin}/blog/${a.slug}`,
      datePublished: a.published_at,
      author: { "@type": "Person", name: a.author_name },
    })),
  };

  return (
    <>
      <SEO
        title="Blog · Conseils mécanique auto, moto et poids lourd"
        description="Guides pratiques, astuces mécaniques et témoignages pour trouver la bonne pièce détachée. Rédigés par Olivier et la communauté Autopièce Malin."
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-cyan-500/10 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-8 sm:py-20">
          <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
            Blog Autopièce Malin
          </span>
          <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Guides, astuces & témoignages
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-400">
            Comment trouver une pièce auto avec sa plaque, quelle référence choisir, retour d'expérience
            atelier… Les articles de la communauté et d'Olivier pour acheter malin.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="blog-search-input"
                placeholder="Chercher un sujet, une pièce, un véhicule…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Link to="/blog/soumettre" data-testid="blog-cta-contribute">
              <Button className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
                <PenSquare className="h-4 w-4" />
                Partager mon astuce
              </Button>
            </Link>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="blog-cat-all"
              onClick={() => setSelectedCat(null)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                !selectedCat ? "bg-cyan-500 text-slate-950" : "bg-slate-900/60 text-slate-300 hover:bg-slate-800"
              }`}
            >
              Toutes
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`blog-cat-${c.id}`}
                onClick={() => setSelectedCat(c.id)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  selectedCat === c.id
                    ? "bg-cyan-500 text-slate-950"
                    : "bg-slate-900/60 text-slate-300 hover:bg-slate-800"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 rounded-2xl shimmer" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div
            data-testid="blog-empty"
            className="rounded-2xl border border-slate-800 bg-slate-900/40 p-12 text-center"
          >
            <h2 className="font-heading text-xl font-bold text-slate-200">
              Aucun article pour l'instant
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Les premiers guides arrivent très bientôt. Reviens vite !
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

const ArticleCard = ({ article }) => (
  <Link
    to={`/blog/${article.slug}`}
    data-testid={`blog-article-${article.slug}`}
    className="surface-card-hover group flex flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50 transition hover:-translate-y-0.5 hover:border-cyan-500/40"
  >
    {article.cover_image_url && (
      <div className="aspect-[16/9] overflow-hidden bg-slate-800">
        <img
          src={article.cover_image_url}
          alt={article.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
    )}
    <div className="flex flex-1 flex-col gap-3 p-5">
      <Badge
        variant="outline"
        className="w-fit border-cyan-500/40 bg-cyan-500/10 text-[10px] uppercase tracking-wider text-cyan-300"
      >
        {article.category}
      </Badge>
      <h3 className="font-heading text-xl font-bold leading-snug text-slate-50 group-hover:text-cyan-200">
        {article.title}
      </h3>
      <p className="text-sm text-slate-400 line-clamp-3">{article.excerpt}</p>
      <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1">
          <User className="h-3 w-3" />
          {article.author_name}
        </span>
        {article.published_at && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(article.published_at).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {article.reading_time_min} min
        </span>
        <ArrowRight className="ml-auto h-4 w-4 text-cyan-400 opacity-0 transition group-hover:opacity-100" />
      </div>
    </div>
  </Link>
);
